import { useId, useRef, useState, type ElementType, type ReactNode, type RefObject } from 'react';
import Popover from '@mui/material/Popover';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { alpha, styled, type SxProps, type Theme } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import {
  addDays,
  isSameDay,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import Box from '@/shared/ui/primitives/Box';
import Button from '@/shared/ui/actions/Button';
import Stack from '@/shared/ui/primitives/Stack';
import { TextField, InputAdornment, FormHelperText } from '@/shared/ui/form/FormControls';
import {
  clampDateRange,
  clampDateRangeToMaxSpan,
  emptyDateRange,
  formatDateRangeChip,
  inclusiveDayCount,
  type DateRangeValue,
} from '@/shared/utils/dateRange';
import {
  DATE_RANGE_PRESET_LABELS,
  DEFAULT_DATE_RANGE_PRESETS,
  getDateRangePreset,
  isSameDateRangeValue,
  type DateRangePresetId,
} from '@/shared/utils/dateRangePresets';
import { fieldLabelSx, outlinedFieldSx } from '../styles/formControlStyles';
import { mergeSx } from '../utils/mergeSx';

export type DateRangeFieldProps = {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  label?: ReactNode;
  /** @deprecated Use `label` instead. Kept for call-site compatibility. */
  fromLabel?: string;
  /** @deprecated Use `label` instead. Kept for call-site compatibility. */
  toLabel?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** Inclusive day cap (e.g. 31). Constrains the second click and hides longer presets. */
  maxSpanDays?: number;
  helperText?: ReactNode;
  presets?: DateRangePresetId[] | false;
  /** When set, Reset applies this preset instead of clearing the range. */
  resetPreset?: DateRangePresetId;
  /** Parent tracks preset "Tất cả" (empty range with distinct meaning). */
  isAllRange?: boolean;
  allRangeLabel?: string;
  onPresetApplied?: (presetId: DateRangePresetId | 'reset') => void;
  sx?: SxProps<Theme>;
  /**
   * Custom open trigger (e.g. Asana-style date chips). When set, replaces the
   * outlined TextField; attach `anchorRef` to the clickable root for the popover.
   */
  renderTrigger?: (props: {
    anchorRef: RefObject<HTMLElement | null>;
    open: boolean;
    value: DateRangeValue;
    disabled: boolean;
    onOpen: () => void;
  }) => ReactNode;
};

type RangeDayProps = PickersDayProps & {
  range?: DateRangeValue;
};

const FieldLabelRoot = styled('label')({});

const laterDate = (a?: Date | null, b?: Date | null): Date | undefined => {
  if (!a) return b ?? undefined;
  if (!b) return a;
  return a > b ? a : b;
};

const earlierDate = (a?: Date | null, b?: Date | null): Date | undefined => {
  if (!a) return b ?? undefined;
  if (!b) return a;
  return a < b ? a : b;
};

const RangeDay = ({
  day,
  range = emptyDateRange(),
  sx,
  outsideCurrentMonth,
  ...other
}: RangeDayProps) => {
  const current = startOfDay(day as Date);
  const from = range.from ? startOfDay(range.from) : null;
  const to = range.to ? startOfDay(range.to) : null;

  const isStart = Boolean(from && isSameDay(current, from));
  const isEnd = Boolean(to && isSameDay(current, to));
  const isInBetween = Boolean(
    from &&
      to &&
      !isStart &&
      !isEnd &&
      isWithinInterval(current, { start: from, end: to }),
  );
  const hasRange = Boolean(from && to && !isSameDay(from, to));

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...(hasRange &&
          (isStart || isEnd || isInBetween) && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          }),
        ...(hasRange &&
          isStart && {
            borderTopLeftRadius: '50%',
            borderBottomLeftRadius: '50%',
          }),
        ...(hasRange &&
          isEnd && {
            borderTopRightRadius: '50%',
            borderBottomRightRadius: '50%',
          }),
      }}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={isStart || isEnd}
        disableMargin
        sx={mergeSx(
          {
            borderRadius: '50%',
            ...(isStart || isEnd
              ? {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover, &:focus': {
                    bgcolor: 'primary.dark',
                  },
                }
              : undefined),
          },
          sx,
        )}
      />
    </Box>
  );
};

const DateRangeField = ({
  value,
  onChange,
  label,
  fromLabel,
  toLabel: _toLabel,
  placeholder = 'Chọn khoảng ngày',
  size = 'small',
  disabled = false,
  minDate,
  maxDate,
  maxSpanDays,
  helperText,
  presets = DEFAULT_DATE_RANGE_PRESETS,
  resetPreset,
  isAllRange = false,
  allRangeLabel,
  onPresetApplied,
  sx,
  renderTrigger,
}: DateRangeFieldProps) => {
  const inputId = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const fieldLabel = label ?? fromLabel;

  const displayValue = isAllRange
    ? (allRangeLabel ?? DATE_RANGE_PRESET_LABELS.all)
    : formatDateRangeChip(value);
  const presetIds = (presets === false ? [] : presets).filter((id) => {
    if (id === 'all') return true;
    if (!maxSpanDays) return true;
    const span = inclusiveDayCount(getDateRangePreset(id));
    return span == null || span <= maxSpanDays;
  });

  const pickingEnd = Boolean(maxSpanDays && value.from && !value.to);
  const spanAnchor = value.from ? startOfDay(value.from) : null;
  const calendarMinDate = laterDate(
    minDate,
    pickingEnd && spanAnchor && maxSpanDays
      ? addDays(spanAnchor, 1 - maxSpanDays)
      : null
  );
  const calendarMaxDate = earlierDate(
    maxDate,
    pickingEnd && spanAnchor && maxSpanDays
      ? addDays(spanAnchor, maxSpanDays - 1)
      : null
  );

  const commitRange = (next: DateRangeValue): void => {
    onChange(
      maxSpanDays
        ? clampDateRangeToMaxSpan(clampDateRange(next), maxSpanDays)
        : clampDateRange(next)
    );
  };

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDaySelect = (day: Date | null) => {
    if (!day) {
      return;
    }

    const selected = startOfDay(day);

    // Start a new range when empty or already complete.
    if (!value.from || (value.from && value.to)) {
      onChange(clampDateRange({ from: selected, to: null }));
      return;
    }

    const from = startOfDay(value.from);
    if (selected < from) {
      commitRange({ from: selected, to: from });
      handleClose();
      return;
    }

    commitRange({ from, to: selected });
    handleClose();
  };

  const handlePresetSelect = (id: DateRangePresetId) => {
    commitRange(getDateRangePreset(id));
    onPresetApplied?.(id);
    handleClose();
  };

  const handleReset = () => {
    if (resetPreset) {
      onChange(getDateRangePreset(resetPreset));
      onPresetApplied?.(resetPreset);
    } else {
      onChange(emptyDateRange());
      onPresetApplied?.('reset');
    }
    handleClose();
  };

  const calendarValue = value.to ?? value.from;

  const defaultInput = (
    <Box
      ref={anchorRef as RefObject<HTMLDivElement | null>}
      sx={{ width: '100%' }}
    >
      <TextField
        id={inputId}
        size={size}
        value={displayValue}
        placeholder={placeholder}
        disabled={disabled}
        fullWidth
        onClick={handleOpen}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpen();
          }
        }}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <KeyboardArrowDownIcon
                  fontSize="small"
                  sx={{
                    color: 'action.active',
                    transform: open ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </InputAdornment>
            ),
          },
          htmlInput: {
            'aria-haspopup': 'dialog',
            'aria-expanded': open,
          },
        }}
        sx={mergeSx(outlinedFieldSx, {
          '& .MuiOutlinedInput-root': {
            cursor: disabled ? 'default' : 'pointer',
          },
        })}
      />
    </Box>
  );

  const trigger = renderTrigger
    ? renderTrigger({
        anchorRef,
        open,
        value,
        disabled,
        onOpen: handleOpen,
      })
    : defaultInput;

  return (
    <Box sx={mergeSx({ width: renderTrigger ? 'auto' : '100%' }, sx)}>
      {fieldLabel != null && !renderTrigger ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
          <FieldLabelRoot htmlFor={inputId} sx={fieldLabelSx}>
            {fieldLabel}
          </FieldLabelRoot>
          {trigger}
          {helperText ? (
            <FormHelperText sx={{ mx: 0, mt: 0.5 }}>{helperText}</FormHelperText>
          ) : null}
        </Box>
      ) : (
        <>
          {trigger}
          {helperText ? (
            <FormHelperText sx={{ mx: 0, mt: 0.5 }}>{helperText}</FormHelperText>
          ) : null}
        </>
      )}

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              boxShadow: 3,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ minWidth: { xs: 280, sm: 420 } }}
        >
          {presetIds.length > 0 ? (
            <Stack
              spacing={0.25}
              sx={{
                width: { xs: '100%', sm: 148 },
                py: 1.5,
                px: 1.25,
                bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
              }}
            >
              {presetIds.map((id) => {
                const presetValue = getDateRangePreset(id);
                const selected =
                  id === 'all'
                    ? isAllRange
                    : !isAllRange && isSameDateRangeValue(value, presetValue);

                return (
                  <Button
                    key={id}
                    intent="ghost"
                    size="small"
                    onClick={() => handlePresetSelect(id)}
                    sx={{
                      justifyContent: 'flex-start',
                      px: 1.25,
                      py: 0.75,
                      minHeight: 32,
                      fontWeight: selected ? 600 : 400,
                      color: selected ? 'primary.main' : 'text.primary',
                      bgcolor: selected
                        ? (theme) => alpha(theme.palette.primary.main, 0.1)
                        : 'transparent',
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    {DATE_RANGE_PRESET_LABELS[id]}
                  </Button>
                );
              })}

              <Box sx={{ flex: 1, minHeight: 8 }} />

              <Button
                intent="secondary"
                size="small"
                onClick={handleReset}
                fullWidth
                sx={{
                  justifyContent: 'center',
                  px: 1.25,
                  py: 0.75,
                  minHeight: 32,
                  fontWeight: 600,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
                  },
                }}
              >
                Reset
              </Button>
            </Stack>
          ) : null}

          <Box sx={{ px: 1, py: 0.5 }}>
            <DateCalendar
              value={calendarValue}
              referenceDate={calendarValue ?? undefined}
              onChange={handleDaySelect}
              minDate={calendarMinDate}
              maxDate={calendarMaxDate}
              showDaysOutsideCurrentMonth
              fixedWeekNumber={6}
              slots={{
                day: RangeDay as ElementType<PickersDayProps>,
              }}
              slotProps={{
                day: {
                  range: value,
                } as Partial<PickersDayProps>,
              }}
              sx={{
                width: 300,
                maxHeight: 'none',
                '& .MuiPickersSlideTransition-root': {
                  minHeight: 240,
                },
              }}
            />
          </Box>
        </Stack>
      </Popover>
    </Box>
  );
};

export default DateRangeField;
